import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'
import { connect } from 'react-redux'

import { Filter, LifeInsuranceStep, Product } from 'even-ts-static'

import Parent from 'common/services/Parent'

import { lifeInsurancePhoneCallRequested, stepBack, stepSelected, updateLead } from 'common/actions'
import {
  applicationFieldChanged,
  applicationFieldRegistered,
  applicationFieldUnregistered,
} from 'common/actions/application'
import AdvertiserDisclosure from 'embed/components/AdvertiserDisclosure'
import { BackButton } from 'embed/components/Application/BackButton'
import ApplicationSubmitButton from 'embed/components/ApplicationSubmitButton'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { applicationLifeInsurancePersonalInformationIsValidSelector } from 'embed/selectors/application'
import { FieldDataValue, MultiProductDispatch } from 'embed/constants'

import { AcceptAndContinueLanguage } from '../Application/AcceptAndContinueLanguage'
import { UpdateLeadFailed } from '../common/UpdateLeadFailed'
import { PersonalInfoFields } from './PersonalInfoFields'

import { MultiProductState } from 'embed/reducers'
import { ApiResponse } from 'common/services/Api'

const applicationStyles = require('embed/styles/application')
const styles = require('./styles')

interface OwnProps {
  hideHeader?: boolean
}

interface StoreProps {
  isValid?: boolean
}

interface DispatchProps {
  onBackButtonClick: () => void
  dispatchApplicationFieldChanged: (filter: Filter, value: FieldDataValue) => void
  dispatchApplicationFieldRegistered: (filter: Filter) => void
  dispatchApplicationFieldUnregistered: (filter: Filter) => void
  dispatchStepSelectedRequestACallConfirmation: () => void
  dispatchUpdateLead: () => Promise<void | ApiResponse>
}

type Props = OwnProps & StoreProps & DispatchProps

export const UnconnectedRequestACall: React.SFC<Props> = ({
  dispatchApplicationFieldRegistered,
  dispatchApplicationFieldUnregistered,
  dispatchApplicationFieldChanged,
  dispatchStepSelectedRequestACallConfirmation,
  dispatchUpdateLead,
  hideHeader,
  onBackButtonClick,
  isValid,
}) => {
  React.useEffect(() => {
    dispatchApplicationFieldRegistered(Filter.ConsentsToTCPA)
  }, [])

  React.useEffect(Parent.scrollToFrame, [])

  const [updateLeadFailed, setUpdateLeadFailed] = React.useState(false)
  const [loadingLeadUpdate, setLoadingLeadUpdate] = React.useState(false)

  const updateLeadOrShowError = async (updateLeadAttempts = 1): Promise<boolean | undefined> => {
    // Attempt to patch the lead 3 times. If it fails, show an error.
    setLoadingLeadUpdate(true)

    try {
      await dispatchUpdateLead()
      setLoadingLeadUpdate(false)
      setUpdateLeadFailed(false)
      return true
    } catch (error) {
      // Errors from the leads endpoint are already sent to Sentry in the updateLead action, so
      // just handling it here for the sake of updating the UI
      if (updateLeadAttempts < 3) {
        await updateLeadOrShowError(updateLeadAttempts + 1)
      } else {
        setLoadingLeadUpdate(false)
        setUpdateLeadFailed(true)
        return false
      }
    }
  }

  const onRequestACallClick = async () => {
    if (isValid) {
      dispatchApplicationFieldChanged(Filter.ConsentsToTCPA, true)

      if (await updateLeadOrShowError()) {
        dispatchStepSelectedRequestACallConfirmation()
      }
    }
  }

  const handleBackButtonClick = () => {
    dispatchApplicationFieldUnregistered(Filter.FirstName)
    dispatchApplicationFieldUnregistered(Filter.LastName)
    dispatchApplicationFieldUnregistered(Filter.Email)
    dispatchApplicationFieldUnregistered(Filter.PrimaryPhone)
    dispatchApplicationFieldUnregistered(Filter.ConsentsToTCPA)
    onBackButtonClick()
  }

  return (
    <ThemeConsumer>
      {({ backdropStyles, borderRadiusLarge, boxStyles, colorBorder }) => (
        <div>
          {!hideHeader && (
            <>
              <div
                className={classnames(
                  styles.header,
                  css`
                    background-color: ${backdropStyles.backgroundColor};
                    border-color: ${colorBorder};
                    border-radius: ${borderRadiusLarge};
                  `,
                )}
                style={boxStyles}
              >
                <BackButton onClick={handleBackButtonClick} />
                <div className={styles.message}>
                  <h2>Request a Call</h2>
                  <p>Submit your information and someone from our team will be in touch with you.</p>
                </div>
              </div>
              <div className={styles.advertiserDisclosure}>
                <AdvertiserDisclosure />
              </div>
            </>
          )}
          <PersonalInfoFields />
          <SubmitButtonRequestACall
            onRequestACallClick={onRequestACallClick}
            isValid={!!isValid}
            loading={loadingLeadUpdate}
          />
          {updateLeadFailed && <UpdateLeadFailed />}
        </div>
      )}
    </ThemeConsumer>
  )
}

const SubmitButtonRequestACall: React.SFC<{
  isValid: boolean
  onRequestACallClick: () => Promise<void>
  loading: boolean
}> = ({ isValid, onRequestACallClick, loading }) => (
  <>
    <ApplicationSubmitButton isValid={isValid} onSubmit={onRequestACallClick} loading={loading}>
      <span className={applicationStyles.consentButtonLabel}>Agree and Request a Call</span>
    </ApplicationSubmitButton>
    <AcceptAndContinueLanguage className={applicationStyles.consentWithButton} />
  </>
)

const mapStateToProps = (state: MultiProductState) => ({
  isValid: applicationLifeInsurancePersonalInformationIsValidSelector(state),
})

const mapDispatchToProps = (dispatch: MultiProductDispatch) => ({
  onBackButtonClick: () => dispatch(stepBack(Product.LifeInsurance)),
  dispatchApplicationFieldChanged: (filter: Filter, value: FieldDataValue) =>
    dispatch(applicationFieldChanged(Product.LifeInsurance, filter, value)),
  dispatchApplicationFieldRegistered: (filter: Filter) =>
    dispatch(applicationFieldRegistered(Product.LifeInsurance, filter)),
  dispatchApplicationFieldUnregistered: (filter: Filter) =>
    dispatch(applicationFieldUnregistered(Product.LifeInsurance, filter)),
  dispatchUpdateLead: () => dispatch(updateLead(Product.LifeInsurance)),
  dispatchStepSelectedRequestACallConfirmation: () => {
    dispatch(stepSelected(Product.LifeInsurance, LifeInsuranceStep.RequestACallConfirmation))
    dispatch(lifeInsurancePhoneCallRequested())
  },
})

export const RequestACall = connect<StoreProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(UnconnectedRequestACall)
